import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Inbox, Users, CalendarCheck, GraduationCap, Sun, Megaphone, Trash2, Plus, LogOut, Loader2, Star, Calendar } from "lucide-react";

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [annForm, setAnnForm] = useState({ title: "", body: "" });
  const [testForm, setTestForm] = useState({ name: "", role: "Parent", quote: "", rating: 5 });
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState({ title: "", description: "", date: "Coming Soon", time: "", location: "", format: "In-person", audience: "Educators" });
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (user === false || (user && user.role !== "admin")) nav("/login");
  }, [user, nav]);

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/inquiries").then(({ data }) => setInquiries(data)).catch(() => {});
    api.get("/announcements").then(({ data }) => setAnnouncements(data)).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  };
  useEffect(() => { if (user && user.role === "admin") loadAll(); }, [user]); // eslint-disable-line

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/inquiries/${id}`, { status }); loadAll(); toast.success("Updated"); }
    catch { toast.error("Failed to update"); }
  };

  const addAnnouncement = async () => {
    if (!annForm.title || !annForm.body) return toast.error("Fill in title and message");
    try { await api.post("/admin/announcements", annForm); setAnnForm({ title: "", body: "" }); loadAll(); toast.success("Announcement posted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delAnnouncement = async (id) => { await api.delete(`/admin/announcements/${id}`); loadAll(); toast.success("Deleted"); };

  const addTestimonial = async () => {
    if (!testForm.name || !testForm.quote) return toast.error("Fill in name and quote");
    try { await api.post("/admin/testimonials", testForm); setTestForm({ name: "", role: "Parent", quote: "", rating: 5 }); loadAll(); toast.success("Testimonial added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delTestimonial = async (id) => { await api.delete(`/admin/testimonials/${id}`); loadAll(); toast.success("Deleted"); };

  const addEvent = async () => {
    if (!eventForm.title || !eventForm.description) return toast.error("Fill in title and description");
    try { await api.post("/admin/events", eventForm); setEventForm({ title: "", description: "", date: "Coming Soon", time: "", location: "", format: "In-person", audience: "Educators" }); loadAll(); toast.success("Event added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delEvent = async (id) => { await api.delete(`/admin/events/${id}`); loadAll(); toast.success("Deleted"); };

  if (!user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;

  const filtered = filter === "all" ? inquiries : inquiries.filter((i) => i.type === filter);
  const statCards = stats ? [
    { icon: Inbox, label: "Total Inquiries", value: stats.total_inquiries, color: "bg-navy" },
    { icon: Megaphone, label: "New", value: stats.new_inquiries, color: "bg-coral" },
    { icon: CalendarCheck, label: "Tour Requests", value: stats.tours, color: "bg-mustard" },
    { icon: GraduationCap, label: "Enrollments", value: stats.enrollments, color: "bg-navy" },
    { icon: Sun, label: "Summer Camp", value: stats.summer_camp, color: "bg-coral" },
    { icon: Users, label: "Parents", value: stats.parents, color: "bg-mustard" },
  ] : [];

  const badge = (t) => ({ tour: "bg-mustard/20 text-mustard-dark", enrollment: "bg-emerald-100 text-emerald-700", summer_camp: "bg-coral/20 text-coral-dark", contact: "bg-navy/10 text-navy" }[t] || "bg-gray-100");

  return (
    <div className="min-h-screen bg-cream pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div><div className="text-coral font-semibold tracking-widest uppercase text-xs">Admin Panel</div><h1 className="font-heading text-3xl font-bold text-navy">Dashboard</h1></div>
          <Button variant="outline" onClick={() => { logout(); nav("/"); }} className="rounded-full gap-2" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Log Out</Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statCards.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-black/5" data-testid={`stat-${i}`}>
              <div className={`h-10 w-10 rounded-xl ${s.color} flex items-center justify-center text-white mb-3`}><s.icon className="h-5 w-5" /></div>
              <div className="font-heading text-2xl font-extrabold text-navy">{s.value}</div>
              <div className="text-xs text-navy/50 font-semibold">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="inquiries">
          <TabsList className="bg-white rounded-full p-1 h-auto flex-wrap">
            <TabsTrigger value="inquiries" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="admin-tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="announcements" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="admin-tab-announcements">Announcements</TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="admin-tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="events" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="admin-tab-events">Educator Events</TabsTrigger>
          </TabsList>

          {/* INQUIRIES */}
          <TabsContent value="inquiries" className="mt-6">
            <div className="flex gap-2 mb-4 flex-wrap">
              {["all", "tour", "enrollment", "summer_camp", "contact"].map((f) => (
                <button key={f} onClick={() => setFilter(f)} data-testid={`inq-filter-${f}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold capitalize ${filter === f ? "bg-navy text-white" : "bg-white text-navy/70 border border-black/5"}`}>{f.replace("_", " ")}</button>
              ))}
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
              {filtered.length === 0 && <div className="p-8 text-center text-navy/50">No inquiries yet.</div>}
              {filtered.map((q) => (
                <div key={q.id} className="p-5 border-b border-black/5 last:border-0 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`inquiry-row-${q.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-navy">{q.parent_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${badge(q.type)}`}>{q.type.replace("_", " ")}</span>
                      {q.status === "new" && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">New</span>}
                    </div>
                    <div className="text-sm text-navy/60 mt-1">{q.email} {q.phone && `· ${q.phone}`}</div>
                    {q.child_name && <div className="text-sm text-navy/60">Child: {q.child_name} {q.child_age && `(${q.child_age})`}</div>}
                    {q.program_interest && <div className="text-sm text-navy/60">Interest: {q.program_interest}</div>}
                    {q.preferred_date && <div className="text-sm text-navy/60">Preferred: {q.preferred_date}</div>}
                    {q.message && <div className="text-sm text-navy/70 mt-1 italic">"{q.message}"</div>}
                    <div className="text-xs text-navy/40 mt-1">{new Date(q.created_at).toLocaleString()}</div>
                  </div>
                  <Select value={q.status} onValueChange={(v) => setStatus(q.id, v)}>
                    <SelectTrigger className="w-36 rounded-full" data-testid={`inq-status-${q.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="toured">Toured</SelectItem>
                      <SelectItem value="enrolled">Enrolled</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ANNOUNCEMENTS */}
          <TabsContent value="announcements" className="mt-6 grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 h-fit">
              <h3 className="font-heading font-bold text-navy mb-4">Post Announcement</h3>
              <div className="space-y-3">
                <Input placeholder="Title" value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} data-testid="ann-title-input" />
                <Textarea placeholder="Message" rows={4} value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} data-testid="ann-body-input" />
                <Button onClick={addAnnouncement} className="rounded-full bg-coral hover:bg-coral-dark text-white gap-1.5" data-testid="ann-post-btn"><Plus className="h-4 w-4" /> Post</Button>
              </div>
            </div>
            <div className="space-y-3">
              {announcements.map((a) => (
                <div key={a.id} className="bg-white rounded-2xl p-5 shadow-sm border border-black/5" data-testid={`admin-ann-${a.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-heading font-bold text-navy">{a.title}</h4>
                    <Button variant="ghost" size="icon" onClick={() => delAnnouncement(a.id)} className="text-red-500 h-8 w-8 shrink-0" data-testid={`ann-del-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-sm text-navy/60 mt-1">{a.body}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* TESTIMONIALS */}
          <TabsContent value="testimonials" className="mt-6 grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 h-fit">
              <h3 className="font-heading font-bold text-navy mb-4">Add Testimonial</h3>
              <div className="space-y-3">
                <Input placeholder="Family name" value={testForm.name} onChange={(e) => setTestForm({ ...testForm, name: e.target.value })} data-testid="test-name-input" />
                <Input placeholder="Role (e.g. Parent of Liam)" value={testForm.role} onChange={(e) => setTestForm({ ...testForm, role: e.target.value })} data-testid="test-role-input" />
                <Textarea placeholder="Their words..." rows={3} value={testForm.quote} onChange={(e) => setTestForm({ ...testForm, quote: e.target.value })} data-testid="test-quote-input" />
                <Button onClick={addTestimonial} className="rounded-full bg-coral hover:bg-coral-dark text-white gap-1.5" data-testid="test-add-btn"><Plus className="h-4 w-4" /> Add</Button>
              </div>
            </div>
            <div className="space-y-3">
              {testimonials.map((tm) => (
                <div key={tm.id} className="bg-white rounded-2xl p-5 shadow-sm border border-black/5" data-testid={`admin-test-${tm.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex text-mustard">{Array.from({ length: tm.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-mustard" />)}</div>
                    <Button variant="ghost" size="icon" onClick={() => delTestimonial(tm.id)} className="text-red-500 h-8 w-8 shrink-0" data-testid={`test-del-${tm.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-sm text-navy/70 mt-2 italic">"{tm.quote}"</p>
                  <div className="text-sm font-semibold text-navy mt-2">{tm.name} <span className="text-navy/40 font-normal">· {tm.role}</span></div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* EDUCATOR EVENTS */}
          <TabsContent value="events" className="mt-6 grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 h-fit">
              <h3 className="font-heading font-bold text-navy mb-4">Add Educator Event</h3>
              <div className="space-y-3">
                <Input placeholder="Title" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} data-testid="event-title-input" />
                <Textarea placeholder="Description" rows={3} value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} data-testid="event-desc-input" />
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Date (e.g. Coming Soon)" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} data-testid="event-date-input" />
                  <Input placeholder="Time" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} data-testid="event-time-input" />
                  <Input placeholder="Location" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} data-testid="event-location-input" />
                  <Input placeholder="Format (In-person / Online)" value={eventForm.format} onChange={(e) => setEventForm({ ...eventForm, format: e.target.value })} data-testid="event-format-input" />
                </div>
                <Button onClick={addEvent} className="rounded-full bg-coral hover:bg-coral-dark text-white gap-1.5" data-testid="event-add-btn"><Plus className="h-4 w-4" /> Add Event</Button>
              </div>
            </div>
            <div className="space-y-3">
              {events.map((ev) => (
                <div key={ev.id} className="bg-white rounded-2xl p-5 shadow-sm border border-black/5" data-testid={`admin-event-${ev.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-heading font-bold text-navy">{ev.title}</h4>
                    <Button variant="ghost" size="icon" onClick={() => delEvent(ev.id)} className="text-red-500 h-8 w-8 shrink-0" data-testid={`event-del-${ev.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-sm text-navy/60 mt-1">{ev.description}</p>
                  <div className="text-xs text-navy/40 mt-2 flex flex-wrap gap-x-3 gap-y-1"><span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{ev.date}</span><span>{ev.time}</span><span>{ev.location}</span><span className="text-mustard-dark font-semibold">{ev.format}</span></div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
